import React from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useIntl } from 'react-intl';
import moment from 'moment';

import axios from '../util/Api.js';
import { formatMeasurement } from '../util/formatMeasurement.js';
import getConnectorPowerKw from '../util/ConnectorCalculatePower.js';
import { getLocationData } from '../util/getLocationData.js';

const statusConfig = {
  waiting:  { icon: 'ri-lock-unlock-fill', color: 'text-yellow-400', pulse: true },
  charging: { icon: 'ri-flashlight-fill',  color: 'text-accent',     pulse: true },
  idle:     { icon: 'ri-parking-box-fill',  color: 'text-orange-400', pulse: false },
  rejected: { icon: 'ri-error-warning-fill', color: 'text-danger',    pulse: false },
  closed:   { icon: 'ri-check-double-fill', color: 'text-success',    pulse: false },
  error:    { icon: 'ri-error-warning-fill', color: 'text-danger',    pulse: false },
};

export default function Charging() {
  const [locData, setLocData] = React.useState({});
  const [state, setState] = React.useState({
    status: 'waiting',
    statusMessage: null,
    timestamp: '-',
    pricing: null,
    currency: '-',
    chargingTime: 0,
    idleTime: 0,
    transaction_kwh: 0,
    power_active_import: null,
    transaction_soc: null,
    initializing: true,
  });

  const navigate = useNavigate();
  const intl = useIntl();
  const { evseId, sessionId } = useParams();

  const refreshTimer = React.useRef(null);
  const sessionNotFoundRetryCounter = React.useRef(0);

  const setSessionData = React.useCallback(() => {
    clearTimeout(refreshTimer.current);
    axios
      .get(`checkouts/${sessionId}`)
      .then(({ data }) => {
        if (data.remote_request_status === 'Rejected') {
          setState((prevState) => ({ ...prevState, status: 'rejected' }));
        } else if (data.id && data.transaction_start_time) {
          const transaction = { ...data };
          const newState = {
            ...transaction,
            status: 'charging',
            timestamp: moment().format('DD-MM-YYYY HH:mm:ss'),
          };

          if (transaction.transaction_start_time) {
            const moment_start = moment.utc(transaction.transaction_start_time);
            if (transaction.transaction_end_time) {
              newState.status = 'closed';
              const charge_end = transaction.charging_end_time
                ? moment.utc(transaction.charging_end_time)
                : moment.utc(transaction.transaction_end_time);
              newState.chargingTime = charge_end.diff(moment_start, 'seconds');
              if (transaction.charging_end_time) {
                newState.idleTime = moment.utc(transaction.transaction_end_time).diff(charge_end, 'seconds');
              }
            } else if (transaction.charging_end_time) {
              newState.status = 'idle';
              const charge_end = moment.utc(transaction.charging_end_time);
              newState.chargingTime = charge_end.diff(moment_start, 'seconds');
              newState.idleTime = moment().utc().diff(charge_end, 'seconds');
            } else {
              newState.chargingTime = moment().utc().diff(moment_start, 'seconds');
            }
          }
          setState((previous) => ({ ...previous, ...newState }));
          if (transaction.transaction_end_time) {
            // Idle charging can still incur fees. Show the receipt only once the
            // transaction itself is complete, including any billed idle period.
            navigate(`/receipt/${evseId}/${sessionId}`, { replace: true });
          } else {
            refreshTimer.current = setTimeout(setSessionData, 5 * 1000);
          }
        } else if (data.id && !data.transaction_start_time) {
          sessionNotFoundRetryCounter.current++;
          if (sessionNotFoundRetryCounter.current <= 60) {
            refreshTimer.current = setTimeout(setSessionData, 5000);
          } else {
            setState((prevState) => ({ ...prevState, status: 'error', statusMessage: 'charging.error.sessionnotfound' }));
          }
        }
      })
      .catch((e) => {
        setState((prevState) => ({ ...prevState, status: 'error', statusMessage: e.response?.data?.detail }));
      });
  }, [sessionId, evseId, navigate]);

  React.useEffect(() => {
    if (evseId) {
      axios.get(`evses/${evseId}`).then(async ({ data }) => {
        if (data.id) {
          const location_data = await getLocationData(data);
          setLocData(location_data);
        }
      });
      setSessionData();
    } else {
      navigate('/');
    }
    const refreshOnReturn = () => { if (!document.hidden) setSessionData(); };
    document.addEventListener("visibilitychange", refreshOnReturn);
    window.addEventListener("focus", refreshOnReturn);
    return () => { clearTimeout(refreshTimer.current); document.removeEventListener("visibilitychange", refreshOnReturn); window.removeEventListener("focus", refreshOnReturn); };
  }, [evseId, navigate, setSessionData]);

  const onRefresh = () => {
    clearTimeout(refreshTimer.current);
    setSessionData();
  };

  const [stopping, setStopping] = React.useState(false);

  const onStopCharging = () => {
    setStopping(true);
    axios
      .post(`checkouts/${sessionId}/stop`)
      .then(() => {
        clearTimeout(refreshTimer.current);
        setTimeout(setSessionData, 3000);
      })
      .catch(() => {
        setStopping(false);
        setState((prevState) => ({ ...prevState, status: 'error', statusMessage: 'global.error.generic' }));
      });
  };

  const getFormattedChargingTime = (seconds) => {
    let secs = seconds;
    const hours = secs / 3600;
    secs = secs % 3600;
    const mins = secs / 60;
    secs = secs % 60;
    return `${parseInt(hours, 10)}:${parseInt(mins, 10) < 10 ? `0${parseInt(mins, 10)}` : parseInt(mins, 10)}:${secs < 10 ? `0${parseInt(secs, 10)}` : parseInt(secs, 10)}`;
  };

  const getStatusText = () => {
    if (state.status === 'waiting') return intl.formatMessage({ id: 'charging.authorized.waiting' });
    if (state.status === 'charging') return `${intl.formatMessage({ id: 'charging.speed' })}${state.power_active_import !== null ? `: ${formatMeasurement(state.power_active_import, intl.locale)} kW` : ''}`;
    if (state.status === 'idle') return intl.formatMessage({ id: 'charging.idle' });
    if (state.status === 'rejected') return intl.formatMessage({ id: 'charging.rejected' });
    if (state.status === 'closed') return intl.formatMessage({ id: 'charging.finished' });
    if (state.statusMessage) return intl.formatMessage({ id: state.statusMessage });
    return intl.formatMessage({ id: 'global.error.generic' });
  };

  const cfg = statusConfig[state.status] || statusConfig.error;
  const spendPercent = state.selected_amount && state.pricing?.total_costs_gross != null
    ? Math.min((state.pricing.total_costs_gross / 100 / state.selected_amount) * 100, 100)
    : 0;

  return (
    <div className="min-h-screen w-full bg-brand-gradient flex flex-col items-center px-4 pt-14 pb-8">
      <div className="w-full max-w-md">
        {/* Header */}
        <div className="glass-card p-4 mb-6">
          <div className="flex justify-between items-center text-white text-sm">
            <div className="flex items-center gap-2">
              <i className="ri-ev-charge-line text-accent text-lg"></i>
              <span className="font-bold">{evseId}</span>
            </div>
            <div className="flex items-center gap-3 text-white/60">
              <span>{locData.power_type}</span>
              <span className="text-accent font-bold">
                {getConnectorPowerKw(locData.max_voltage, locData.max_amperage, locData.power_type)} kW
              </span>
            </div>
          </div>
        </div>

        {/* Status hero */}
        <div className="text-center mb-8">
          <div className={`text-6xl mb-4 ${cfg.color} ${cfg.pulse ? 'animate-pulse' : ''}`}>
            <i className={cfg.icon}></i>
          </div>
          <h2 className="text-white text-xl font-black mb-2">{getStatusText()}</h2>
          <p className="text-white/40 text-sm">
            <i className="ri-time-line"></i> {intl.formatMessage({ id: 'charging.lastupdate' })}: {state.timestamp}
          </p>
        </div>

        {/* Action buttons */}
        {(state.status === 'charging' || state.status === 'waiting' || state.status === 'idle') && (
          <div className="mb-6 space-y-3">
            <button onClick={onRefresh} className="w-full py-3 rounded-xl glass-card text-white font-semibold flex items-center justify-center gap-2 active:scale-[0.98] transition-all">
              <i className="ri-refresh-line"></i> {intl.formatMessage({ id: 'charging.refresh' })}
            </button>
            {state.status === 'charging' && (
              <button
                onClick={onStopCharging}
                disabled={stopping}
                className="btn-danger flex items-center justify-center gap-2"
              >
                {stopping ? <i className="ri-loader-4-line animate-spin"></i> : <i className="ri-stop-circle-line"></i>}
                {intl.formatMessage({ id: 'charging.button.stop' })}
              </button>
            )}
          </div>
        )}

        {state.status !== 'rejected' ? (
          <>
            {/* Stats grid */}
            <div className="grid grid-cols-2 gap-3 mb-6">
              {/* Cost */}
              <div className="stat-card">
                <i className="ri-money-euro-circle-line text-accent text-2xl mb-1"></i>
                <span className="text-white/50 text-xs">{intl.formatMessage({ id: 'charging.costs' })}</span>
                <span className="text-white text-xl font-black">
                  {((state.pricing?.total_costs_gross || 0) / 100).toFixed(2)}
                </span>
                <span className="text-white/40 text-xs">{state.pricing?.currency}</span>
              </div>
              {/* Time */}
              <div className="stat-card">
                <i className="ri-timer-line text-accent text-2xl mb-1"></i>
                <span className="text-white/50 text-xs">{intl.formatMessage({ id: 'charging.time' })}</span>
                <span className="text-white text-xl font-black">{getFormattedChargingTime(state.chargingTime)}</span>
              </div>
              {/* Energy */}
              <div className="stat-card">
                <i className="ri-flashlight-line text-accent text-2xl mb-1"></i>
                <span className="text-white/50 text-xs">{intl.formatMessage({ id: 'charging.energy' })}</span>
                <span className="text-white text-xl font-black">{formatMeasurement(state.transaction_kwh ?? 0, intl.locale)}</span>
                <span className="text-white/40 text-xs">kWh</span>
              </div>
              {/* SoC or Power */}
              <div className="stat-card">
                {state.transaction_soc !== null ? (
                  <>
                    <i className="ri-battery-charge-line text-accent text-2xl mb-1"></i>
                    <span className="text-white/50 text-xs">{intl.formatMessage({ id: 'charging.soc' })}</span>
                    <span className="text-white text-xl font-black">{formatMeasurement(state.transaction_soc, intl.locale)}%</span>
                    {state.target_soc && <span className="text-white/40 text-xs">→ {formatMeasurement(state.target_soc, intl.locale)}%</span>}
                  </>
                ) : (
                  <>
                    <i className="ri-speed-line text-accent text-2xl mb-1"></i>
                    <span className="text-white/50 text-xs">Power</span>
                    <span className="text-white text-xl font-black">{formatMeasurement(state.power_active_import, intl.locale)}</span>
                    <span className="text-white/40 text-xs">kW</span>
                  </>
                )}
              </div>
            </div>

            {/* Spending limit bar */}
            {state.selected_amount && state.pricing?.total_costs_gross != null && state.status === 'charging' && (
              <div className="glass-card p-4 mb-4">
                <div className="flex justify-between text-xs text-white/60 mb-2">
                  <span>{(state.pricing.total_costs_gross / 100).toFixed(2)} / {state.selected_amount.toFixed(2)} {state.pricing.currency}</span>
                  <span>{intl.formatMessage({ id: 'charging.autostop.info' }, { limit: `${state.selected_amount.toFixed(2)} ${state.pricing.currency}` })}</span>
                </div>
                <div className="w-full bg-white/10 rounded-full h-3 overflow-hidden">
                  <div
                    className={`h-full rounded-full transition-all duration-500 ${spendPercent >= 90 ? 'bg-danger' : 'bg-accent'}`}
                    style={{ width: `${spendPercent}%` }}
                  ></div>
                </div>
              </div>
            )}

            {/* SoC progress */}
            {state.transaction_soc !== null && state.target_soc && (
              <div className="glass-card p-4 mb-4">
                <div className="flex justify-between text-xs text-white/60 mb-2">
                  <span>{intl.formatMessage({ id: 'charging.soc.target' }, { current: formatMeasurement(state.transaction_soc, intl.locale), target: formatMeasurement(state.target_soc, intl.locale) })}</span>
                </div>
                <div className="w-full bg-white/10 rounded-full h-3 overflow-hidden">
                  <div
                    className={`h-full rounded-full transition-all duration-500 ${state.transaction_soc >= state.target_soc ? 'bg-success' : 'bg-emerald-500'}`}
                    style={{ width: `${Math.min((state.transaction_soc / state.target_soc) * 100, 100)}%` }}
                  ></div>
                </div>
              </div>
            )}

            {/* Idle info */}
            {(state.status === 'idle' || (state.status === 'closed' && state.idleTime > 0)) && (
              <div className="glass-card p-4 mb-4 space-y-2">
                <div className="flex justify-between text-sm">
                  <span className="text-white/60">{intl.formatMessage({ id: 'charging.idle.time' })}</span>
                  <span className="text-white font-bold">{getFormattedChargingTime(state.idleTime)}</span>
                </div>
                {state.pricing?.idle_costs != null && (
                  <div className="flex justify-between text-sm">
                    <span className="text-white/60">{intl.formatMessage({ id: 'charging.idle.costs' })}</span>
                    <span className="text-white font-bold">{(state.pricing.idle_costs / 100).toFixed(2)} {state.pricing.currency}</span>
                  </div>
                )}
                {state.status === 'idle' && (
                  <p className="text-xs text-white/40 text-center pt-2">{intl.formatMessage({ id: 'charging.idle.info' })}</p>
                )}
              </div>
            )}

            {/* View Receipt button */}
            {state.status === 'closed' && (
              <button
                onClick={() => navigate(`/receipt/${evseId}/${sessionId}`)}
                className="w-full py-3 rounded-xl bg-accent text-white font-semibold flex items-center justify-center gap-2 active:scale-[0.98] transition-all mt-4"
              >
                <i className="ri-file-text-line"></i>
                {intl.formatMessage({ id: 'charging.button.receipt' })}
              </button>
            )}
          </>
        ) : (
          /* Rejected */
          <div className="text-center space-y-4">
            <p className="text-white/70 text-sm">{intl.formatMessage({ id: 'charging.dontworry' })}</p>
            <p className="text-white/50 text-sm">{intl.formatMessage({ id: 'charging.tryagain' })}</p>
            <button
              className="btn-primary flex items-center justify-center gap-2"
              onClick={() => navigate(`/checkout/${evseId}`)}
            >
              <i className="ri-flashlight-line"></i> {intl.formatMessage({ id: 'charging.button.again' })}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
